import React, { useMemo, useRef } from "react"
import { ThemeProvider } from "styled-components"

import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import { formatCurrency } from "utils/currency"
import useTracking from "hooks/useTracking"

import AnnouncementBar from "components/announce-bar"
import AnnouncementDropdown from "components/announcement-dropdown"
import ContentfulTextBlurb from "./text"
import ContentfulButton from "./button"

const ContentfulAnnouncementBar = ({
  component,
  nested,
  isBefore,
  isMobile,
  hideOnMobile,
  subNavButton,
}) => {
  const {
    id,
    announcements,
    ctaButton,
    secondaryCTAButton,
    countdownTimerEndDate,
    backgroundColor,
    mobileLayout,
  } = component

  const { shopifyProductVariant: pageShopifyProductVariant } = useComponentizedPage()

  const ref = useRef(null)
  const { trackView, track } = useTracking()

  let button = null
  let buttonSubscription = null

  const hasBuyOnceAndSubscriptionButtons =
    (ctaButton?.action === "Add to Cart" || ctaButton?.action === "Add Subscription to Cart") &&
    (secondaryCTAButton?.action === "Add to Cart" ||
      secondaryCTAButton?.action === "Add Subscription to Cart")
  if (hasBuyOnceAndSubscriptionButtons) {
    button = ctaButton.action === "Add to Cart" ? ctaButton : secondaryCTAButton
    buttonSubscription =
      ctaButton.action === "Add Subscription to Cart" ? ctaButton : secondaryCTAButton
  }

  const productCartData = useMemo(() => {
    const variant = buttonSubscription?.shopifyProductVariant || pageShopifyProductVariant
    if (variant) {
      const price = parseFloat(variant.price)

      const sellingPlan = variant?.skioSellingPlanGroupResource?.sellingPlan

      const discount =
        sellingPlan && sellingPlan.fixedPrice ? parseFloat(sellingPlan.fixedPrice) : null

      const finalPrice =
        discount && !Number.isNaN(discount) ? price - price * (discount / 100) : price

      return {
        productId: variant.variantId,
        discountedPrice: formatCurrency(finalPrice.toFixed(2), true),
        regularPrice: price,
        productTitle: variant.product.title,
        quantity: buttonSubscription?.quantity,
        redirect: !!buttonSubscription?.goToCart,
        variant,
      }
    }
  }, [buttonSubscription, pageShopifyProductVariant])
  if (announcements[0]?.isWebinarPromotion) {
    trackView(
      ref,
      ctaButton?.trackingId,
      { promotionId: ctaButton?.trackingId, position: "header" },
      (props) => track("Promotion Viewed", props),
    )
  }

  return (
    <ThemeProvider theme={componentThemeExtension(nested ? null : backgroundColor, nested)}>
      <AnnouncementBar
        dataId={id}
        isBefore={isBefore}
        isMobile={isMobile}
        hideOnMobile={hideOnMobile}
        mobileLayout={mobileLayout}
        announceElements={announcements.map((a) => (
          <ContentfulTextBlurb key={a.id} component={a} nested elRef={ref} />
        ))}
        dropdownAndButtonElement={
          hasBuyOnceAndSubscriptionButtons && (
            <AnnouncementDropdown
              regularPrice={productCartData?.regularPrice}
              subscriptionPrice={productCartData?.discountedPrice}
              buttonElement={
                button && (
                  <ContentfulButton
                    component={{
                      ...button,
                      buttonColor: "Primary CTA",
                      text: "Add to cart",
                      size: "Small",
                      disclaimerText: null,
                      discountText: null,
                    }}
                  />
                )
              }
              buttonSubscriptionElement={
                buttonSubscription && (
                  <ContentfulButton
                    component={{
                      ...buttonSubscription,
                      buttonColor: "Primary CTA",
                      text: "Add to cart",
                      size: "Small",
                      disclaimerText: null,
                      discountText: null,
                      isWebinarPromotion: announcements[0]?.isWebinarPromotion,
                    }}
                  />
                )
              }
            />
          )
        }
        buttonElement={
          ctaButton && (
            <ContentfulButton
              component={{
                ...ctaButton,
                size: "Small",
                disclaimerText: null,
                discountText: null,
                isWebinarPromotion: announcements[0]?.isWebinarPromotion,
              }}
            />
          )
        }
        secondaryButtonElement={
          secondaryCTAButton && (
            <ContentfulButton
              component={{
                ...secondaryCTAButton,
                size: "Small",
                disclaimerText: null,
                discountText: null,
                isWebinarPromotion: announcements[0]?.isWebinarPromotion,
              }}
            />
          )
        }
        subNavButton={
          subNavButton && (
            <ContentfulButton
              component={{
                ...subNavButton,
                size: "Small",
                disclaimerText: null,
                discountText: null,
                isWebinarPromotion: announcements[0]?.isWebinarPromotion,
              }}
            />
          )
        }
        endTime={countdownTimerEndDate}
      />
    </ThemeProvider>
  )
}

export default ContentfulAnnouncementBar
